import { useState, useEffect } from "react";
import { DaisyAtlaOption } from "../../models/daisyatlaoption.model";
import { responseHero } from "../../utils/axiosInterceptor";
import { toast } from "react-toastify"; // vagy amennyiben toastify-t / egyéb toast könyvtárat használsz

interface AddBonusAtlaModalProps {
    isOpen: boolean;
    item: DaisyAtlaOption | null;
    onClose: () => void;
    onSuccess?: (updatedItem: DaisyAtlaOption, newQuantity: string) => void;
}

export default function AddBonusAtlaModal({
    isOpen,
    item,
    onClose,
    onSuccess
}: AddBonusAtlaModalProps) {
    const [bonusInput, setBonusInput] = useState<string>("");
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        if (item) {
            setBonusInput(item.bonusAtlaQuantity || "");
        } else {
            setBonusInput("");
        }
    }, [item]);

    if (!isOpen || !item) return null;

    const handleSubmit = async () => {
        setIsSaving(true);

        // 1. Negatív számok és törtszámok kezelése:
        // Math.max(0, ...) biztosítja, hogy ha negatívat írt be, az 0 legyen.
        const parsedValue = Math.floor(Number(bonusInput) || 0);
        const validatedValue = Math.max(0, parsedValue).toString();

        // 2. API hívás a megadott backend struktúra alapján (req.body.data.bonusAtlaQuantity)
        await responseHero.patch(`${process.env.REACT_APP_API_URL}/daisy/atla/optionslist/${item.id}`, {
            data: {
                bonusAtlaQuantity: validatedValue
            }
        })
        .then((response) => {
            toast.success("Sikeresen frissítetted a bónusz mennyiséget!");
            
            if (onSuccess) {
                onSuccess(item, validatedValue);
            }
            onClose();
        })
        .catch((error) => {
            console.error("Hiba a mentés során:", error?.response?.data?.message || error);
            toast.error("Sikertelen mentés!");
        })
        .finally(() => {
            setIsSaving(false);
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-in">
            <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 shadow-xl border border-slate-100 transition-all transform scale-100">
                <h3 className="text-base font-semibold text-slate-900 border-b border-slate-100 pb-3 mb-4">
                    ATLA coinok beállítása
                </h3>

                <div className="space-y-4">
                    <div>
                        <span className="text-xs font-medium text-slate-400 block uppercase tracking-wider">
                            Felhasználó
                        </span>
                        <p className="text-sm font-semibold text-slate-800">
                            {item.user?.name ?? "Ismeretlen"}
                        </p>
                    </div>

                    <div>
                        <label htmlFor="bonusAmount" className="text-xs font-medium text-slate-500 block mb-1.5">
                            ATLA coin darabszám beállítása:
                        </label>
                        <input
                            id="bonusAmount"
                            type="number"
                            step="1"
                            min="0"
                            value={bonusInput}
                            onChange={(e) => setBonusInput(e.target.value)}
                            placeholder="Pl. 250"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:border-pink-500 focus:outline-none focus:ring-1 focus:ring-pink-500"
                        />
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t border-slate-100 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-4 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors disabled:opacity-50"
                    >
                        Mégse
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="px-4 py-2 text-xs font-semibold rounded-lg bg-pink-600 text-white shadow-sm hover:bg-pink-700 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                    >
                        {isSaving ? "Mentés..." : "Mentés"}
                    </button>
                </div>
            </div>
        </div>
    );
}